/**
 * Cutting the silence off both ends of every phrase the second voice speaks
 * (task 6).
 *
 * Three staging calls pointed here. The more phrases a call was cut into, the
 * further the second voice fell behind the built-in one — 0.35 s with 21
 * phrases, 3.6 s with 30, 5.5 s with 58 — and the gap grew through each call
 * rather than staying put. The provider itself answered in about 0.16 s every
 * time. What grows with the number of phrases, and not with the number of
 * words, is whatever each phrase carries besides its words: the quiet the
 * provider puts before the first word and after the last. Laid end to end,
 * as the player lays them, those margins become a queue that never drains.
 *
 * So a phrase's leading silence is dropped, apart from a few milliseconds so
 * the first consonant is not clipped, and its trailing silence is held back
 * and dropped when the provider says the phrase is over. Silence *inside* a
 * phrase — the pause after a comma — is kept: it is held only until the next
 * word arrives and then played in its place, so the phrase keeps its rhythm.
 *
 * Pure and clockless, fed arrays of samples, so it is tested without audio.
 */

/** Below this a sample is silence: -40 dBFS, well under speech, above the provider's floor. */
export const SILENCE_LEVEL = 0.01;
/** Kept before the first loud sample, so an onset is not clipped. */
export const PRE_ROLL_S = 0.03;
/** Kept after the last loud sample, so a phrase fades rather than stops. */
export const TAIL_S = 0.06;

/** Samples on a plain ArrayBuffer, which is what Web Audio will copy from. */
type Samples = Float32Array<ArrayBuffer>;

export class TurnTrimmer {
  readonly #rate: number;
  #speaking = false;
  /** Quiet samples since the last loud one, held until we know what they are. */
  #held: Samples[] = [];
  #trimmed = 0;

  constructor(sampleRate: number) {
    this.#rate = sampleRate;
  }

  /** Samples dropped so far, in seconds. */
  get trimmedS(): number {
    return this.#trimmed / this.#rate;
  }

  /** Whether the current phrase has produced any sound yet. */
  get speaking(): boolean {
    return this.#speaking;
  }

  /** One chunk of a phrase in; what should be played now out. */
  push(samples: Samples): Samples {
    let from = 0;
    if (!this.#speaking) {
      const first = firstLoud(samples);
      if (first < 0) {
        this.#trimmed += samples.length;
        return EMPTY;
      }
      from = Math.max(0, first - Math.round(PRE_ROLL_S * this.#rate));
      this.#trimmed += from;
      this.#speaking = true;
    }

    const last = lastLoud(samples);
    if (last < from) {
      // Quiet all through: a pause inside the phrase, or its end. Not known
      // yet which, so it waits.
      this.#held.push(samples.subarray(from));
      return EMPTY;
    }

    const out = join([...this.#held, samples.subarray(from, last + 1)]);
    this.#held = last + 1 < samples.length ? [samples.subarray(last + 1)] : [];
    return out;
  }

  /**
   * The provider says the phrase is over: what was held was its trailing
   * silence. A short tail of it is played and the rest dropped, and the next
   * phrase starts trimming its lead-in afresh.
   */
  end(): Samples {
    const held = join(this.#held);
    this.#held = [];
    this.#speaking = false;
    const keep = Math.min(held.length, Math.round(TAIL_S * this.#rate));
    this.#trimmed += held.length - keep;
    return held.subarray(0, keep);
  }
}

const EMPTY: Samples = new Float32Array(0);

function firstLoud(s: Float32Array): number {
  for (let i = 0; i < s.length; i += 1) if (Math.abs(s[i]!) > SILENCE_LEVEL) return i;
  return -1;
}

function lastLoud(s: Float32Array): number {
  for (let i = s.length - 1; i >= 0; i -= 1) if (Math.abs(s[i]!) > SILENCE_LEVEL) return i;
  return -1;
}

function join(parts: Samples[]): Samples {
  if (parts.length === 1) return parts[0]!;
  const out = new Float32Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}
